import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import equalityFn from 'zustand/shallow';
import { IS_DEVELOPMENT } from '~/modules/config';
import { Routes } from '~/modules/routes';
import { ListingResponse } from '~/modules/services/listing_service';
import { handleException } from '~/modules/utilities/error_utilities';
import { Marketplace } from '~/modules/utilities/language_utils';
import { isExternalAgentListing, selectExtensionListing } from '~/modules/utilities/listing_utils';
import { getPublisherListings } from '../services/moreFromProviderService';
import { listingStore } from './listingStore';
import { routerStore, selectMatch } from './routerStore';

export const MIN_PROVIDER_LISTINGS_TO_DISPLAY = 1;
// The listing-detail page requests this many listings so the "More from {provider}"
// section can be a paginated carousel. Passed by the listing-detail component when
// it drives the fetch on Overview activation (see `listingDetailV2.fetchMoreFromProvider`).
export const V2_PROVIDER_LISTINGS_TO_DISPLAY = 10;

export enum MoreFromThisProviderLoadingStatus {
    UNINITILIAZED = 'UNINITILIAZED',
    LOADED = 'LOADED',
    LOADING = 'LOADING',
    ERROR = 'ERROR',
}
export interface MoreFromProviderState extends State {
    loadingStatus: MoreFromThisProviderLoadingStatus;
    data: {
        listings: ListingResponse[];
        publisherId: string;
    };
    listingId: string;
}

const initialState: MoreFromProviderState = {
    loadingStatus: MoreFromThisProviderLoadingStatus.UNINITILIAZED,
    data: {
        listings: [],
        publisherId: '',
    },
    listingId: '',
};

export const moreFromProviderStore = createStore(
    initialState,

    //(set: SetState<MoreFromProviderState>, get: GetState<MoreFromProviderState>) => ({
    (set: SetState<MoreFromProviderState>, _get: GetState<MoreFromProviderState>) => ({
        async fetchProviderListings(listingId: string, publisherId: string, marketplace?: Marketplace, count?: number) {
            try {
                set(function setLoading(draft) {
                    draft.loadingStatus = MoreFromThisProviderLoadingStatus.LOADING;
                    draft.listingId = listingId;
                });

                const data = await getPublisherListings(publisherId, listingId, marketplace, count);

                set(function loadListings(draft: MoreFromProviderState) {
                    draft.data.listings = data;
                    draft.data.publisherId = publisherId;
                    draft.loadingStatus = MoreFromThisProviderLoadingStatus.LOADED;
                });
            } catch (error) {
                handleException(error, 'moreFromProviderStore.fetchProviderListings', {
                    level: 'warn',
                    debugData: { listingId, publisherId },
                });
                set(function failedLoadListings(draft: MoreFromProviderState) {
                    draft.loadingStatus = MoreFromThisProviderLoadingStatus.ERROR;
                });
            }
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'moreFromProviderStore',
    IS_DEVELOPMENT,
);

/**
 * Fetch this listing's "More from {provider}" set unless it is already loaded.
 * The single source of truth for the fetch **gate**: the listing-detail component
 * drives it on Overview activation (see `listingDetailV2.fetchMoreFromProvider`).
 *
 * Skips external-agent listings and listings missing a publisher/listing id,
 * and dedupes against an in-flight or loaded fetch for the same listing so
 * repeated activations don't re-fire. `count` selects the carousel depth
 * (`V2_PROVIDER_LISTINGS_TO_DISPLAY`); when omitted the request falls back to the
 * service default.
 */
export function ensureProviderListings(listing: ListingResponse | undefined, count?: number): void {
    // Skills and plugins use their curated listing relationships instead of the
    // publisher's general inventory, so they never request publisher listings.
    if (isExternalAgentListing(listing)) {
        return;
    }

    const listingId = listing?.external_id;
    const publisherId = listing?.publisher?.external_id;
    if (!listingId || !publisherId) {
        return;
    }

    const state = moreFromProviderStore.getState();
    if (state.loadingStatus !== MoreFromThisProviderLoadingStatus.UNINITILIAZED && listingId === state.listingId) {
        return;
    }

    const marketplace: Marketplace = selectExtensionListing(listing)?.marketplace ?? 'English';
    moreFromProviderStore.fetchProviderListings(listingId, publisherId, marketplace, count);
}

function checkListingId() {
    const { params, url } = selectMatch(routerStore.getState());

    if (Routes.LISTING !== url) {
        moreFromProviderStore.reset();
        return;
    }

    // @ts-expect-error * Automated Type Error Fix
    if (!params.listingId) {
        return;
    }

    // @ts-expect-error * Automated Type Error Fix
    const listing = listingStore.getState().listings.get(params.listingId);

    // Clear any previous listing's results when navigating to an external-agent
    // listing (skills/plugins). This reset is a subscription-lifecycle concern
    // and must run on every route/listing change — distinct from
    // `ensureProviderListings`, which skips the *fetch* for the same listings.
    if (isExternalAgentListing(listing)) {
        if (moreFromProviderStore.getState().loadingStatus !== MoreFromThisProviderLoadingStatus.UNINITILIAZED) {
            moreFromProviderStore.reset();
        }
    }

    // The listing-detail page (v2) drives this fetch from the component instead:
    // it fires only when the Overview tab activates (see
    // `listingDetailV2.fetchMoreFromProvider`), so a deep link to
    // Reviews/Specs/Security/etc. no longer pays the provider request. The reset
    // above still runs on every route or listing change, so stale data is cleared
    // regardless. See docs/W-23636498-overviewv2-changes.md.
}

routerStore.subscribe(checkListingId, selectMatch, equalityFn);
listingStore.subscribe(checkListingId);
checkListingId();
